import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box, Button, Card, Typography, TextField,
  CircularProgress, Skeleton, Snackbar, Alert, InputAdornment,
  Avatar, FormControlLabel, Switch,
} from "@mui/material";
import {
  Building2, Mail, Phone, FileText, Upload, Image as ImageIcon,
  Globe, Save, X, Settings as SettingsIcon,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import AdminShell from "../layout/AdminShell";
import { adminGetSiteConfiguration, adminCreateSiteConfiguration, adminUpdateSiteConfiguration } from "../../api/Siteconfiguration";
import { resolveMediaUrl } from "../../utils/mediaUrl";

const DEFAULT_VALUES = {
  organization_name: "",
  support_email: "",
  support_phone: "",
  footer_text: "",
  is_maintenance_mode: false,
};

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[+\d][\d\s-]{6,18}$/;

export default function SiteConfigurationPage() {
  const qc = useQueryClient();
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  const logoObjectUrl = useRef(null);
  const faviconObjectUrl = useRef(null);

  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const { control, handleSubmit, reset, watch, formState: { errors, isDirty } } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const filesChanged = !!logoFile || !!faviconFile;
  const hasUnsavedChanges = isDirty || filesChanged;

  const { data: config, isLoading, isError, error } = useQuery({
    queryKey: ["admin-site-configuration"],
    queryFn: () => adminGetSiteConfiguration().then(r => r.data),
    retry: false,
  });

  const notFound = isError && error?.response?.status === 404;

  const applyConfigToForm = useCallback((cfg) => {
    reset({
      organization_name: cfg?.organization_name ?? "",
      support_email: cfg?.support_email ?? "",
      support_phone: cfg?.support_phone ?? "",
      footer_text: cfg?.footer_text ?? "",
      is_maintenance_mode: cfg?.is_maintenance_mode ?? false,
    });
    setLogoPreview(resolveMediaUrl(cfg?.organization_logo_url || cfg?.organization_logo));
    setFaviconPreview(resolveMediaUrl(cfg?.favicon_url || cfg?.favicon));
    setLogoFile(null);
    setFaviconFile(null);
  }, [reset]);

  useEffect(() => {
    if (config) applyConfigToForm(config);
  }, [config, applyConfigToForm]);

  useEffect(() => () => {
    if (logoObjectUrl.current) URL.revokeObjectURL(logoObjectUrl.current);
    if (faviconObjectUrl.current) URL.revokeObjectURL(faviconObjectUrl.current);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (!hasUnsavedChanges) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  const createMutation = useMutation({
    mutationFn: (fd) => adminCreateSiteConfiguration(fd),
    onSuccess: (r) => {
      qc.setQueryData(["admin-site-configuration"], r.data);
      applyConfigToForm(r.data);
      notify("Site configuration created");
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't create site configuration", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: (fd) => adminUpdateSiteConfiguration(fd),
    onSuccess: (r) => {
      qc.setQueryData(["admin-site-configuration"], r.data);
      applyConfigToForm(r.data);
      notify("Site configuration saved");
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't save site configuration", "error"),
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (values) => {
    const fd = new FormData();
    fd.append("organization_name", values.organization_name);
    
    if (values.support_email) fd.append("support_email", values.support_email);
    if (values.support_phone) fd.append("support_phone", values.support_phone);
    if (values.footer_text) fd.append("footer_text", values.footer_text);
    if (values.is_maintenance_mode !== undefined) fd.append("is_maintenance_mode", values.is_maintenance_mode);

    if (logoFile) fd.append("organization_logo", logoFile);
    if (faviconFile) fd.append("favicon", faviconFile);

    if (config?.id) updateMutation.mutate(fd);
    else createMutation.mutate(fd);
  };

  const validateAndPreviewImage = (file, setPreview, setFile, objectUrlRef) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      notify("Please choose an image file", "error");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      notify("Image must be 2MB or smaller", "error");
      return;
    }
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setPreview(url);
    setFile(file);
  };

  const handleLogoChange = (e) => {
    validateAndPreviewImage(e.target.files?.[0], setLogoPreview, setLogoFile, logoObjectUrl);
  };

  const handleFaviconChange = (e) => {
    validateAndPreviewImage(e.target.files?.[0], setFaviconPreview, setFaviconFile, faviconObjectUrl);
  };

  const handleDiscard = () => {
    if (config) applyConfigToForm(config);
    else {
      reset(DEFAULT_VALUES);
      setLogoPreview(null);
      setFaviconPreview(null);
      setLogoFile(null);
      setFaviconFile(null);
    }
    notify("Changes discarded", "info");
  };

  return (
    <AdminShell title="Site Configuration">
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 520 }}>
          Global settings for the public site — organization identity, support contact details, and footer text.
          {notFound && " No configuration exists yet, so saving this form will create it."}
        </Typography>
      </Box>

      {isLoading ? (
        <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 3, maxWidth: 800 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Skeleton variant="rounded" height={44} />
            <Skeleton variant="rounded" height={44} />
            <Skeleton variant="rounded" height={44} />
            <Skeleton variant="rounded" height={80} />
            <Skeleton variant="rounded" height={80} />
          </Box>
        </Card>
      ) : isError && !notFound ? (
        <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 5, maxWidth: 800, textAlign: "center" }}>
          <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(239,68,68,0.07)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
            <SettingsIcon size={22} color="#DC2626" />
          </Box>
          <Typography fontWeight={700} color="#0F172A" mb={0.5}>Couldn't load site configuration</Typography>
          <Typography color="text.secondary" fontSize={13.5}>
            {error?.response?.data?.message || "Something went wrong. Please try again."}
          </Typography>
        </Card>
      ) : (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {/* Organization Section */}
          <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", maxWidth: 800, mb: 2.5 }}>
            <Box sx={{ 
              background: "linear-gradient(135deg, rgba(37,99,235,0.08) 0%, rgba(37,99,235,0.12) 100%)",
              px: 2.5, py: 2, 
              display: "flex", 
              alignItems: "center", 
              gap: 1
            }}>
              <Building2 size={18} color="#2563EB" />
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>
                Organization Information
              </Typography>
            </Box>
            <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.25 }}>
              <Controller
                name="organization_name"
                control={control}
                rules={{ required: "Organization name is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Organization Name"
                    fullWidth
                    disabled={saving}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Building2 size={15} color="#94A3B8" /></InputAdornment> }}
                  />
                )}
              />
              <Controller
                name="footer_text"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Footer Text"
                    fullWidth
                    multiline
                    rows={2}
                    disabled={saving}
                    placeholder="Shown in the site footer, e.g. a copyright line"
                    InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.25 }}><FileText size={15} color="#94A3B8" /></InputAdornment> }}
                  />
                )}
              />
            </Box>
          </Card>

          {/* Contact Section */}
          <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", maxWidth: 800, mb: 2.5 }}>
            <Box sx={{ 
              background: "linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(34,197,94,0.12) 100%)",
              px: 2.5, py: 2, 
              display: "flex", 
              alignItems: "center", 
              gap: 1
            }}>
              <Phone size={18} color="#16A34A" />
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>
                Contact Information
              </Typography>
            </Box>
            <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.25 }}>
              <Controller
                name="support_email"
                control={control}
                rules={{ pattern: { value: EMAIL_PATTERN, message: "Enter a valid email address" } }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Support Email"
                    fullWidth
                    disabled={saving}
                    type="email"
                    placeholder="support@yourcompany.com"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Mail size={15} color="#94A3B8" /></InputAdornment> }}
                  />
                )}
              />
              <Controller
                name="support_phone"
                control={control}
                rules={{ pattern: { value: PHONE_PATTERN, message: "Enter a valid phone number" } }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Support Phone"
                    fullWidth
                    disabled={saving}
                    placeholder="e.g. +91 90909 09090"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Phone size={15} color="#94A3B8" /></InputAdornment> }}
                  />
                )}
              />
            </Box>
          </Card>

          {/* Branding Section */}
          <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", maxWidth: 800, mb: 2.5 }}>
            <Box sx={{ 
              background: "linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(245,158,11,0.12) 100%)",
              px: 2.5, py: 2, 
              display: "flex", 
              alignItems: "center", 
              gap: 1
            }}>
              <ImageIcon size={18} color="#B45309" />
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>
                Branding
              </Typography>
            </Box>
            <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.25 }}>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A", mb: 1 }}>Organization Logo</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    variant="rounded"
                    src={logoPreview || undefined}
                    sx={{ width: 52, height: 52, borderRadius: "12px", bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB", flexShrink: 0 }}
                  >
                    <ImageIcon size={20} />
                  </Avatar>
                  <Button
                    component="label" 
                    variant="outlined" 
                    size="small" 
                    disabled={saving}
                    startIcon={<Upload size={14} />}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", borderColor: "rgba(15,23,42,0.15)", color: "#0F172A" }}
                  >
                    {logoPreview ? "Replace logo" : "Upload logo"}
                    <input type="file" accept="image/*" hidden onChange={handleLogoChange} />
                  </Button>
                  {logoFile && (
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{logoFile.name}</Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                  Recommended: 200x60px, PNG or SVG, max 2MB
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A", mb: 1 }}>Favicon</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    variant="rounded"
                    src={faviconPreview || undefined}
                    sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB", flexShrink: 0 }}
                  >
                    <Globe size={18} />
                  </Avatar>
                  <Button
                    component="label" 
                    variant="outlined" 
                    size="small" 
                    disabled={saving}
                    startIcon={<Upload size={14} />}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", borderColor: "rgba(15,23,42,0.15)", color: "#0F172A" }}
                  >
                    {faviconPreview ? "Replace favicon" : "Upload favicon"}
                    <input type="file" accept="image/*" hidden onChange={handleFaviconChange} />
                  </Button>
                  {faviconFile && (
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{faviconFile.name}</Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                  Recommended: 32x32px or 64x64px, ICO or PNG, max 2MB
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Advanced Section */}
          <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", maxWidth: 800, mb: 2.5 }}>
            <Box sx={{ 
              background: "linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(139,92,246,0.12) 100%)",
              px: 2.5, py: 2, 
              display: "flex", 
              alignItems: "center", 
              gap: 1
            }}>
              <SettingsIcon size={18} color="#7C3AED" />
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>
                Advanced Settings
              </Typography>
            </Box>
            <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.25 }}>
              <Controller
                name="is_maintenance_mode"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={e => field.onChange(e.target.checked)} disabled={saving} color="primary" />}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <SettingsIcon size={16} color={field.value ? "#DC2626" : "#6B7280"} />
                        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
                          {field.value ? "Maintenance mode is ON - Site is down for visitors" : "Maintenance mode is OFF - Site is live"}
                        </Typography>
                      </Box>
                    }
                    disabled={saving}
                  />
                )}
              />
            </Box>
          </Card>

          {/* Actions */}
          <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", maxWidth: 800 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 3 }}>
              {hasUnsavedChanges && (
                <Button
                  type="button" 
                  variant="text" 
                  disabled={saving} 
                  onClick={handleDiscard}
                  startIcon={<X size={16} />}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", color: "text.secondary" }}
                >
                  Discard Changes
                </Button>
              )}
              <Button
                type="submit" 
                variant="contained" 
                disabled={saving || !hasUnsavedChanges}
                startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <Save size={16} />}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#2563EB", px: 2.5, minWidth: 140, "&:hover": { bgcolor: "#1D4ED8" } }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </Box>
          </Card>
        </Box>
      )}

      <Snackbar
        open={snackbar.open} 
        autoHideDuration={3500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))} 
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}